import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import API_CONFIG from '../utils/apiConfig';
import './RegisterForm.css';

const RegisterForm = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite_token');
  
  const formLoadTimeRef = useRef(Date.now());
  /** Uncontrolled honeypot: avoids browser/password-manager autofill setting React state and tripping the bot check. */
  const registerHoneypotRef = useRef(null);
  const registerHoneypotIdRef = useRef(
    `reg-hp-${typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`
  );
  const [formData, setFormData] = useState({
    firstName: '',
    surname: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [gdprConsentGiven, setGdprConsentGiven] = useState(false);
  const navigate = useNavigate();

  // Validate invite token on mount if present
  useEffect(() => {
    if (inviteToken) {
      validateInvite();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken]);

  const validateInvite = async () => {
    setValidatingInvite(true);
    try {
      const response = await fetch(API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.validateInviteToken}/${inviteToken}`));
      if (response.ok) {
        const data = await response.json();
        setInviteInfo(data);
        setFormData(prev => ({ ...prev, email: data.email }));
        
        // If user already exists, show message to log in
        if (data.user_exists) {
          setError('An account with this email already exists. Please log in to accept the invite.');
        }
      } else {
        const errorData = await response.json();
        setError(
          errorData.detail ||
            'This invitation is not valid. It may have expired or been withdrawn by the employer.'
        );
      }
    } catch (err) {
      setError('Failed to validate invite. Please try again.');
    } finally {
      setValidatingInvite(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear success message when user starts typing
    if (success) {
      setSuccess('');
    }
    
    // Clear error message when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (String(registerHoneypotRef.current?.value || '').trim()) {
      setError('Invalid submission. Please try again.');
      return;
    }
    const elapsedSec = (Date.now() - formLoadTimeRef.current) / 1000;
    if (elapsedSec < 3) {
      setError('Please take a moment to complete the form before submitting.');
      return;
    }

    setLoading(true);

    // Require GDPR consent if registering via invite
    if (inviteToken && !gdprConsentGiven) {
      setError('Please confirm GDPR consent to share your data with the employer');
      setLoading(false);
      return;
    }

    if (inviteToken && inviteInfo && !inviteInfo.user_exists) {
      if (!String(formData.firstName || '').trim()) {
        setFieldErrors({ firstName: 'First name is required' });
        setError('First name is required');
        setLoading(false);
        return;
      }
      if (!String(formData.surname || '').trim()) {
        setFieldErrors({ surname: 'Surname is required' });
        setError('Surname is required');
        setLoading(false);
        return;
      }
    }

    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      const passwordError = 'Passwords do not match';
      setFieldErrors({
        confirmPassword: passwordError
      });
      setError(passwordError);
      setLoading(false);
      return;
    }

    try {
      // Build URL with invite_token as query parameter if present
      let registerUrl = API_CONFIG.getApiUrl(API_CONFIG.endpoints.register);
      if (inviteToken) {
        registerUrl += `?invite_token=${encodeURIComponent(inviteToken)}`;
      }
      
      const response = await fetch(registerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: String(formData.firstName || '').trim(),
          surname: String(formData.surname || '').trim(),
          email: formData.email,
          password: formData.password,
          confirm_password: formData.confirmPassword,
          gdpr_consent_given: inviteToken ? gdprConsentGiven : undefined,
          form_load_time: formLoadTimeRef.current
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const successMessage =
          data.message ||
          (inviteToken
            ? 'Registration successful! Your account is ready to use.'
            : 'Registration successful! Please check your email to verify your account.');
        setSuccess(successMessage);
        if (data.access_token) {
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('token_type', data.token_type || 'bearer');
          if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
          }
          navigate('/dashboard');
          return;
        }
        if (!inviteToken && data.email_verified === false) {
          setFormData((prev) => ({
            ...prev,
            password: '',
            confirmPassword: '',
          }));
          setShowSuccessModal(true);
          return;
        }
      } else {
        // Handle different error formats and show in toast
        let errorMessage = 'Registration failed';
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map(err => 
            typeof err === 'string' ? err : err.msg || err.message || 'Validation error'
          ).join(', ');
        } else if (data.detail && typeof data.detail === 'object') {
          errorMessage = data.detail.msg || data.detail.message || 'Validation error';
        } else if (data.message) {
          errorMessage = data.message;
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      const networkError = 'Network error. Please try again.';
      setError(networkError);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="register-form-container">
      <Helmet>
        <title>Register | PayCIS</title>
        <meta name="description" content="Create your PayCIS account to manage Construction Industry Scheme payments, subcontractor verification, and HMRC returns." />
        <meta property="og:title" content="Register | PayCIS" />
      </Helmet>
      <div className="register-form">
        <div className="form-header">
          <h2>Create Account</h2>
          <p>{inviteInfo ? `You've been invited by ${inviteInfo.employer_company_name || inviteInfo.employer_name}` : 'Join PayCIS and start managing your payments'}</p>
        </div>
        
        {validatingInvite && (
          <div style={{ padding: '15px', textAlign: 'center', color: '#57bedd' }}>
            Validating invite...
          </div>
        )}
        
        {inviteInfo && !inviteInfo.user_exists && (
          <div style={{
            padding: '15px',
            marginBottom: '20px',
            background: 'rgba(91, 192, 222, 0.1)',
            border: '1px solid rgba(91, 192, 222, 0.3)',
            borderRadius: '8px',
            color: '#57bedd'
          }}>
            <strong>✓ Invite Valid</strong>
            <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
              Your email has been verified. You can proceed with registration.
            </p>
          </div>
        )}
        
        {inviteInfo && inviteInfo.user_exists && (
          <div style={{
            padding: '15px',
            marginBottom: '20px',
            background: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: '8px',
            color: '#ffc107'
          }}>
            <strong>⚠ Account Already Exists</strong>
            <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
              An account with this email already exists. Please <Link to={`/login?invite_token=${inviteToken}`} style={{ color: '#57bedd', textDecoration: 'underline' }}>log in</Link> to accept the invite.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {(!inviteToken || (inviteInfo && !inviteInfo.user_exists)) && (
            <>
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required={!inviteToken || !!(inviteInfo && !inviteInfo.user_exists)}
                  placeholder="Enter your first name"
                  className={fieldErrors.firstName ? 'error' : ''}
                />
                {fieldErrors.firstName && (
                  <div className="field-error">{fieldErrors.firstName}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="surname">Surname</label>
                <input
                  type="text"
                  id="surname"
                  name="surname"
                  value={formData.surname}
                  onChange={handleInputChange}
                  required={!inviteToken || !!(inviteInfo && !inviteInfo.user_exists)}
                  placeholder="Enter your surname"
                  className={fieldErrors.surname ? 'error' : ''}
                />
                {fieldErrors.surname && (
                  <div className="field-error">{fieldErrors.surname}</div>
                )}
              </div>

            </>
          )}

          {/* Honeypot: uncontrolled + readOnly so autofill does not populate it and block real users. */}
          <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
            <input
              ref={registerHoneypotRef}
              type="text"
              id={registerHoneypotIdRef.current}
              tabIndex={-1}
              autoComplete="off"
              readOnly
              defaultValue=""
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              disabled={!!inviteInfo && !inviteInfo.user_exists}
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="Enter your email"
              style={inviteInfo && !inviteInfo.user_exists ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                placeholder="Create a password"
                minLength="6"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                placeholder="Confirm your password"
                minLength="6"
                className={fieldErrors.confirmPassword ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {fieldErrors.confirmPassword && (
              <div className="field-error">{fieldErrors.confirmPassword}</div>
            )}
          </div>

          {/* Data Sharing Consent - only for invite flow, shown AFTER email/password */}
          {inviteInfo && !inviteInfo.user_exists && (
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '16px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #dee2e6',
              color: '#333'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
                Data Sharing Consent
              </h3>
              <p style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.6', color: '#495057' }}>
                By accepting this invitation, you authorise PayCIS to share your registration and tax information 
                (name, business details, UTR, NI, verification status, payment information) with the contractor 
                listed below for the purpose of verifying your CIS status and processing payments.
              </p>
              <p style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.6', color: '#495057' }}>
                This data will be accessible only to that contractor's authorised users and will not be visible to others. 
                You can remove the connection at any time from your account.
              </p>
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={gdprConsentGiven}
                    onChange={(e) => setGdprConsentGiven(e.target.checked)}
                    style={{ marginTop: '4px', marginRight: '8px', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '14px', lineHeight: '1.5', color: '#495057' }}>
                    I confirm that I authorise PayCIS to share my registration and tax information with {inviteInfo.employer_company_name || inviteInfo.employer_name || ' the employer'} 
                    &nbsp;for the purpose of verifying my CIS status and processing payments.
                  </span>
                </label>
              </div>
            </div>
          )}

          <button type="submit" className="register-btn" disabled={loading || (inviteToken && !gdprConsentGiven)}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
          
          {error && <div className="error-message">{error}</div>}
        </form>

        <div className="login-link">
          <p>Already have an account? <Link to="/login">Sign in</Link></p>
        </div>

        {showSuccessModal && (
          <div className="success-modal-overlay" onClick={() => {}}>
            <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="success-modal-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h2 className="success-modal-title">Check your email</h2>
              <p className="success-modal-message">
                {success ||
                  'We sent a verification link to your email. Click the link to verify your account, then sign in.'}
              </p>
              <button
                type="button"
                className="success-modal-button"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/login');
                }}
              >
                Go to sign in
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterForm;